import { createMemo } from "solid-js";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { useBillingStatus, useUsage } from "../api";
import type {
  EligibilityAction,
  EligibilityVerdict,
} from "../utils/eligibility";
import { getEligibility } from "../utils/eligibility";

export type UseEligibilityResult = {
  /** Reactive eligibility verdict for the guarded entry point. */
  verdict: () => EligibilityVerdict;
  /** True only while billing/usage is actively fetching (not when disabled/idle). */
  isLoading: () => boolean;
  /** Whether a company is currently selected (the guard redirects when false). */
  hasCompany: () => boolean;
  isAuthenticated: () => boolean;
};

/**
 * Composes the cached billing/usage queries into a single eligibility verdict.
 *
 * Adds no new network call beyond the already-cached `useBillingStatus` / `useUsage`
 * (TanStack dedupes by key). The verdict only consumes data from a *resolved* query;
 * while loading, disabled, or errored it passes `undefined`, which `getEligibility`
 * fails **open** on — a paying customer is never blocked on slow/failed data (D7 / A.12).
 */
export const useEligibility = (
  action: EligibilityAction,
): UseEligibilityResult => {
  const [auth] = useAuthV2();
  const [store] = useCompanyStore();
  const billingStatus = useBillingStatus();
  const usage = useUsage();

  const isAuthenticated = () => !!auth.profile?.id;
  const hasCompany = () => !!store.selectedCompany?.id;

  const verdict = createMemo<EligibilityVerdict>(() =>
    getEligibility({
      subscriptionStatus: billingStatus.isSuccess
        ? billingStatus.data?.billing_status
        : undefined,
      usage: usage.isSuccess ? usage.data?.usage : undefined,
      action,
    }),
  );

  const isLoading = () => billingStatus.isLoading || usage.isLoading;

  return { verdict, isLoading, hasCompany, isAuthenticated };
};
